import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import { Points as ThreePoints } from 'three';
import * as random from 'maath/random/dist/maath-random.esm';
import scienceImage from '../assets/images/our-science-01.png';

const FloatingMolecules = () => {
  const ref = useRef<ThreePoints>(null!);
  const particles = React.useMemo(() => {
    return random.inSphere(new Float32Array(2000), { radius: 7 });
  }, []);

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.x = state.clock.elapsedTime * 0.05;
      ref.current.rotation.y = state.clock.elapsedTime * 0.1;
    }
  });

  return (
    <Points ref={ref} positions={particles} stride={3}>
      <PointMaterial
        transparent
        color="#4ade80"
        size={0.1}
        sizeAttenuation={true}
        depthWrite={false}
      />
    </Points>
  );
};

const OurScience = () => {
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  return (
    <section id="science" className="relative min-h-screen overflow-hidden bg-gradient-to-br from-gray-900 to-emerald-900 flex items-center justify-center">
      {/* 3D Molecular Background */}
      <div className="absolute inset-0 opacity-20">
        <Canvas camera={{ position: [0, 0, 15], fov: 75 }}>
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} intensity={0.5} />
          <FloatingMolecules />
        </Canvas>
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-7xl px-6 py-12">
        {/* "Our Science" Header */}
        <motion.h2 
          className="text-4xl md:text-5xl font-bold mb-12 text-center bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-blue-300"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Our Science
        </motion.h2>

        <motion.div 
          className="flex flex-col lg:flex-row items-center justify-center gap-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          {/* Large Image with Zoom Effect */}
          <motion.div
            className="w-full lg:w-1/2 cursor-pointer"
            initial={{ y: 50, opacity: 0 }}
            animate={{ 
              y: 0, 
              opacity: 1,
              transition: { 
                y: { type: "spring", stiffness: 50 },
                opacity: { duration: 1 }
              }
            }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setIsImageExpanded(!isImageExpanded)}
          >
            <motion.div
              className="relative"
              animate={{
                scale: isImageExpanded ? 1.8 : 1,
                zIndex: isImageExpanded ? 50 : 0
              }}
              transition={{ type: "spring", stiffness: 100 }}
            >
              <img 
                src={scienceImage}
                alt="Our Science"
                className="w-full max-w-2xl mx-auto rounded-2xl shadow-2xl border-4 border-emerald-400/20"
              />
              {isImageExpanded && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl">
                  <p className="text-white text-lg font-semibold">Click to zoom out</p>
                </div>
              )}
            </motion.div>
          </motion.div>

          {/* Text Content */}
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: 1,
                transition: { staggerChildren: 0.1, delayChildren: 0.5 }
              }}
            >
              <motion.h3
                className="text-3xl font-bold mb-6 text-emerald-300"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
              >
                Revolutionary Genetic Science
              </motion.h3>

              <motion.p 
                className="text-lg text-gray-200 leading-relaxed mb-6"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
              >
                Our platform leverages precise 3'UTR engineering
              </motion.p>

              <motion.p 
                className="text-lg text-gray-200 leading-relaxed mb-8"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                We're revolutionizing the way we understand and treat disease through precise genetic interventions.
              </motion.p>
            </motion.div>

            <motion.button
              whileHover={{ 
                scale: 1.05,
                background: "linear-gradient(45deg, #4ade80, #60a5fa)",
                boxShadow: "0 0 20px rgba(74, 222, 128, 0.5)"
              }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-blue-500 text-white font-semibold shadow-lg transition-all duration-300 mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              Explore Our Research
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Overlay for expanded image */}
      {isImageExpanded && (
        <div 
          className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setIsImageExpanded(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img 
              src={scienceImage}
              alt="Our Science (Expanded View)"
              className="rounded-xl shadow-xl w-full h-full object-contain"
            />
            <button 
              className="absolute -top-4 -right-4 bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg"
              onClick={(e) => {
                e.stopPropagation();
                setIsImageExpanded(false);
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default OurScience;